import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/admin/BookingsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CreditCard, Plus, Trash2, Star } from "lucide-react";

const KIND_OPTIONS = [
  { v: "cash", l: "Cash" }, { v: "card_manual", l: "Card in vehicle (EFTPOS)" },
  { v: "stripe", l: "Stripe (online card)" }, { v: "bank_transfer", l: "Bank transfer" },
  { v: "paypal", l: "PayPal" }, { v: "apple_pay", l: "Apple Pay" },
  { v: "google_pay", l: "Google Pay" }, { v: "other", l: "Other" },
];

export default function PaymentMethodsTab() {
  const [methods, setMethods] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "cash", instructions: "", enabled: true, is_default: false });

  const load = async () => {
    try { const { data } = await api.get("/payment-methods"); setMethods(data); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payment-methods", form);
      toast.success("Payment method added"); setOpen(false);
      setForm({ name: "", kind: "cash", instructions: "", enabled: true, is_default: false }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (m, key) => {
    try {
      const patch = { [key]: !m[key] };
      await api.patch(`/payment-methods/${m.id}`, patch);
      toast.success("Updated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setDefault = async (m) => {
    try { await api.patch(`/payment-methods/${m.id}`, { is_default: true, enabled: true }); toast.success(`${m.name} is now default`); load(); }
    catch { toast.error("Failed"); }
  };

  const updateInstructions = async (m, value) => {
    try { await api.patch(`/payment-methods/${m.id}`, { instructions: value }); toast.success("Saved"); load(); }
    catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this payment method?")) return;
    try { await api.delete(`/payment-methods/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <Header title="Payment Methods" subtitle="Enable, disable, and configure every way riders pay.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-gold rounded-full" data-testid="payments-add"><Plus className="w-4 h-4 mr-2" /> Add method</Button></DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">Add payment method</DialogTitle></DialogHeader>
            <form onSubmit={add} className="grid gap-4">
              <div><Label className="text-xs uppercase text-slate-400">Display name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="bg-black/30 border-white/10 mt-1 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10 text-white">
                    {KIND_OPTIONS.map(k => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase text-slate-400">Instructions to rider</Label><Textarea rows={2} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="bg-black/30 border-white/10 mt-1" /></div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /> Enabled</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} /> Default</label>
              </div>
              <Button type="submit" className="btn-gold">Save method</Button>
            </form>
          </DialogContent>
        </Dialog>
      </Header>

      <div className="grid md:grid-cols-2 gap-4">
        {methods.map((m) => (
          <div key={m.id} className={`glass-card rounded-2xl p-5 gold-border relative ${m.is_default ? "ring-2 ring-amber-400/40" : ""}`} data-testid={`payment-card-${m.kind}`}>
            {m.is_default && (
              <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-400"><Star className="w-3 h-3 fill-amber-400" /> Default</div>
            )}
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg bg-amber-400/10 grid place-items-center"><CreditCard className="w-5 h-5 text-amber-400" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-lg">{m.name}</div>
                <Badge className="bg-slate-400/20 text-slate-300 border-slate-400/40 border font-mono uppercase text-[10px] tracking-wider mt-1">{m.kind.replace("_", " ")}</Badge>
              </div>
            </div>
            <Textarea rows={2} defaultValue={m.instructions} onBlur={(e) => e.target.value !== m.instructions && updateInstructions(m, e.target.value)} className="bg-black/30 border-white/10 mt-3 text-sm" placeholder="Instructions shown to riders…" />
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-2"><Switch checked={m.enabled} onCheckedChange={() => toggle(m, "enabled")} data-testid={`payment-toggle-${m.kind}`} /> <span className="text-slate-400">Enabled</span></label>
                {!m.is_default && (
                  <button onClick={() => setDefault(m)} className="text-xs text-amber-400 hover:underline" data-testid={`payment-set-default-${m.kind}`}>Make default</button>
                )}
              </div>
              <Button variant="outline" size="icon" onClick={() => del(m.id)} className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
